import { QuoteCreditCheck } from "src/api-service";

/**
 * Client mirror of the backend `CreditCheckDecision`.
 *
 * Exists so the send/order flow can decide at click time whether to show the credit-check step,
 * without a round-trip. The server re-derives the same verdict at each gate, so a wrong or stale
 * client can never let a denied quote through — the worst case is showing the step unnecessarily.
 *
 * Keep in lockstep with `Quote.Business/Util/CreditCheckDecision.cs` — and, for the bypass case
 * below, with `Quote.Business/Util/CreditCheckGate.cs`, which short-circuits before the decision.
 * The spec files on both sides cover the same cases so drift shows up as a failing test.
 */
export enum CreditCheckOutcome {
    /** No usable check on record — offer to run one. Never blocks. */
    NoCheck = "NoCheck",
    /** Provider data older than the validity window. Re-prompts, but does not block. */
    Stale = "Stale",
    Approved = "Approved",
    /** Fresh, relevant and failing. The only outcome that blocks. */
    Denied = "Denied",
}

/**
 * Strip everything non-alphanumeric and lowercase, so "559317-8584" and "5593178584" compare
 * equal. Mirrors `RegistrationNumberUtils.Normalize` on the server — including the fact that
 * letters are preserved, so "SE5593178584" does NOT reduce to the bare number.
 *
 * Stored registration numbers are already normalized; values read off the quote are raw, so both
 * sides must go through this or the comparison would never match and every check would look absent.
 */
function normalizeRegistrationNo(value: string | null | undefined): string {
    if (!value) return "";
    return value.replace(/[^a-z0-9]/gi, "").toLowerCase();
}

function customerChanged(
    creditCheck: QuoteCreditCheck,
    quoteCustomerCode: string | null | undefined,
    quoteRegistrationNo: string | null | undefined,
): boolean {
    const creditCheckCode = creditCheck.customerCode?.trim() || "";
    const quoteCode = quoteCustomerCode?.trim() || "";

    if (creditCheckCode && quoteCode) {
        return creditCheckCode.toLowerCase() !== quoteCode.toLowerCase();
    }
    // One side has a customer code and the other doesn't: the quote moved between a prospect and a
    // real customer, so the stored result no longer describes this quote's counterparty.
    if (!!creditCheckCode !== !!quoteCode) {
        return true;
    }

    const creditCheckRegNo = normalizeRegistrationNo(creditCheck.registrationNo);
    const quoteRegNo = normalizeRegistrationNo(quoteRegistrationNo);
    if (creditCheckRegNo && quoteRegNo) {
        return creditCheckRegNo !== quoteRegNo;
    }

    // Neither identifier is comparable — nothing indicates a change.
    return false;
}

export function decideCreditCheck(
    creditCheck: QuoteCreditCheck | null | undefined,
    quoteCustomerCode: string | null | undefined,
    quoteRegistrationNo: string | null | undefined,
    currentTotalIncVat: number,
    nowMs: number = Date.now(),
): CreditCheckOutcome {
    if (!creditCheck) return CreditCheckOutcome.NoCheck;

    // The provider call is switched off with no synthetic ceiling, so creditScore and creditLimit
    // are placeholder zeros rather than findings — deciding on them would show Denied on every
    // quote. Mirrors the server, where `CreditCheckGate` returns before consulting the decision at
    // all; hence this sits above the customer and staleness checks too, so the two agree case for
    // case. Approved rather than NoCheck because NoCheck would leave the step asking the user to
    // re-run a check that cannot produce anything different.
    if (creditCheck.unscoredBypass) return CreditCheckOutcome.Approved;

    if (customerChanged(creditCheck, quoteCustomerCode, quoteRegistrationNo)) {
        return CreditCheckOutcome.NoCheck;
    }

    // Measured from when the provider DATA was fetched, not when a user last clicked — a re-check
    // served from the report cache must not make old data look freshly verified.
    const fetchedMs = new Date(creditCheck.reportFetchedAtUtc).getTime();
    const validityMs = (creditCheck.validityDays ?? 0) * 24 * 60 * 60 * 1000;
    if (Number.isFinite(fetchedMs) && fetchedMs < nowMs - validityMs) {
        return CreditCheckOutcome.Stale;
    }

    const scoreOk = creditCheck.creditScore >= creditCheck.minimumCreditScore;
    const limitCoversValue = creditCheck.creditLimit >= currentTotalIncVat;

    return scoreOk && limitCoversValue ? CreditCheckOutcome.Approved : CreditCheckOutcome.Denied;
}

/** Whether the current verdict is a denial that a super user has overridden. */
export function isDenialOverridden(
    outcome: CreditCheckOutcome | null | undefined,
    creditCheck: QuoteCreditCheck | null | undefined,
): boolean {
    return outcome === CreditCheckOutcome.Denied && !!creditCheck?.overriddenBy;
}

/**
 * Whether a denial stands. An explicit override clears it for everyone — the override stamp must
 * be present before the server gate passes too, so a missing stamp surfaces as a validation error
 * here rather than a server rejection later.
 *
 * The override holds even if the order total has grown since, matching the server. A fresh check
 * clears `overriddenBy`, so an override never outlives the result it was taken against.
 */
export function isCreditCheckBlocking(
    outcome: CreditCheckOutcome,
    creditCheck: QuoteCreditCheck | null | undefined,
): boolean {
    if (outcome !== CreditCheckOutcome.Denied) return false;
    return !isDenialOverridden(outcome, creditCheck);
}

export function isDeniedByScore(creditCheck: QuoteCreditCheck | null | undefined): boolean {
    return !!creditCheck && creditCheck.creditScore < creditCheck.minimumCreditScore;
}

export function isDeniedByLimit(
    creditCheck: QuoteCreditCheck | null | undefined,
    currentTotalIncVat: number,
): boolean {
    return !!creditCheck && creditCheck.creditLimit < currentTotalIncVat;
}

/**
 * Whether re-running would actually reach the provider. Inside the report cache window the same
 * score and limit come back, so the only thing that can change the outcome is the quote total —
 * offering "run it again" there would promise a fresh answer it cannot deliver.
 */
export function wouldRerunHitProvider(
    creditCheck: QuoteCreditCheck | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    if (!creditCheck) return true;
    // Bypassed: no re-run reaches the provider, cache window or not.
    if (creditCheck.unscoredBypass) return false;
    const fetchedMs = new Date(creditCheck.reportFetchedAtUtc).getTime();
    if (!Number.isFinite(fetchedMs)) return true;
    const cacheMs = (creditCheck.cacheTtlDays ?? 0) * 24 * 60 * 60 * 1000;
    return fetchedMs < nowMs - cacheMs;
}

/**
 * Whether the customer is domestic, i.e. billed in the selling company's own country. The real
 * credit-check flow is only offered for those: lookups on foreign companies are billed per check.
 * A missing country on either side counts as foreign, so an unknown country never triggers a paid
 * lookup.
 *
 * Mirrors `CreditCheckGate.IsDomesticCustomer` on the server, which refuses to run a check otherwise.
 */
export function isDomesticCustomer(
    customerCountryCode: string | null | undefined,
    companyCountryCode: string | null | undefined,
): boolean {
    const customer = customerCountryCode?.trim().toUpperCase() ?? "";
    const company = companyCountryCode?.trim().toUpperCase() ?? "";
    return customer !== "" && customer === company;
}
